import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetInvoiceAssociations,
  useDeleteRebateInvoice,
} from '@/api/rebates/useDeleteRebateInvoice';
import { InvoiceAssociationInfo } from '@/api/rebates/types';
import { useToast } from 'src/context/ToastContext';

interface UseSupplierInvoiceDeleteModalProps {
  onSuccess?: () => void;
}

/**
 * Hook for deleting invoices from the supplier page.
 * Since we're not in the context of a specific agreement,
 * this will always perform a force delete (removes from ALL agreements).
 */
export function useSupplierInvoiceDeleteModal({
  onSuccess,
}: UseSupplierInvoiceDeleteModalProps) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [invoiceInfo, setInvoiceInfo] = useState<InvoiceAssociationInfo | null>(
    null,
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [forceDeleteConfirmText, setForceDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const { mutateAsync: fetchAssociations } = useGetInvoiceAssociations();
  const { mutateAsync: deleteInvoice } = useDeleteRebateInvoice();

  // Step 1: Open modal and fetch association info
  const initiateDelete = async (invoiceId: string) => {
    try {
      const result = await fetchAssociations(invoiceId);
      setInvoiceInfo(result.data);
      setModalOpen(true);
    } catch (error) {
      console.error('Failed to load invoice details:', error);
      showToast({
        message: 'Failed to load invoice details',
        color: 'error',
      });
    }
  };

  // Step 2: Execute delete - always force delete from supplier page
  const confirmDelete = async () => {
    if (!invoiceInfo) return;

    setIsDeleting(true);
    try {
      // For supplier page, we need an agreementId for the API
      // Use the first agreement from associations
      const firstAgreement = invoiceInfo.associations[0];
      if (!firstAgreement) {
        throw new Error('No agreement association found');
      }

      // Always force delete when deleting from supplier page
      const result = await deleteInvoice({
        invoiceId: invoiceInfo.invoiceId,
        agreementId: firstAgreement.agreementId,
        force: true,
      });

      // Success messages based on mode
      let message = '';
      if (result.data.mode === 'remove_association') {
        message = 'Invoice removed from this agreement';
      } else if (result.data.mode === 'cascade_delete') {
        message = 'Invoice deleted permanently';
      } else if (result.data.mode === 'force_delete') {
        message = `Invoice deleted from ${result.data.deleted?.associations || invoiceInfo.associationCount} agreement(s)`;
      }

      showToast({
        message,
        color: 'success',
      });

      // Invalidate supplier invoice line items queries
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key.length > 0 &&
            typeof key[0] === 'string' &&
            key[0].startsWith('rebates/supplier-reported-amounts')
          );
        },
      });

      // Also invalidate rebate invoices queries
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key.length > 0 &&
            typeof key[0] === 'string' &&
            key[0].startsWith('rebates/invoices')
          );
        },
      });

      // Cleanup and refresh
      closeModal();
      onSuccess?.();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      showToast({
        message:
          error instanceof Error ? error.message : 'Failed to delete invoice',
        color: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setInvoiceInfo(null);
    setShowAdvanced(false);
    setForceDeleteConfirmText('');
    setIsDeleting(false);
  };

  return {
    modalOpen,
    invoiceInfo,
    isDeleting,
    showAdvanced,
    forceDeleteConfirmText,
    setShowAdvanced,
    setForceDeleteConfirmText,
    initiateDelete,
    confirmDelete,
    closeModal,
  };
}
